/**
 * Lab Settlement Hook
 * Hook for running and tracking settlement operations
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useState } from 'react';
import type { SettlementResult } from './labDashboardTypes';

export interface UseLabSettlementResult {
  runSettlement: () => Promise<SettlementResult | null>;
  isSettling: boolean;
  lastResult: SettlementResult | null;
  error: string | null;
  clearLastResult: () => void;
}

export function useLabSettlement(): UseLabSettlementResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [isSettling, setIsSettling] = useState(false);
  const [lastResult, setLastResult] = useState<SettlementResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runSettlement = useCallback(async (): Promise<SettlementResult | null> => {
    if (!isAuthenticated) {
      setError('Authentication required');
      return null;
    }

    setIsSettling(true);
    setError(null);

    try {
      const response = await authFetch(`${API_BASE_URL}/api/lab/settle`, {
        method: 'POST',
      });

      const result = await safeParseJson<SettlementResult | ApiErrorResponse>(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      const settlementResult = result as SettlementResult;
      setLastResult(settlementResult);
      return settlementResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run settlement';
      setError(errorMessage);
      return null;
    } finally {
      setIsSettling(false);
    }
  }, [isAuthenticated, authFetch]);

  const clearLastResult = useCallback(() => {
    setLastResult(null);
    setError(null);
  }, []);

  return { runSettlement, isSettling, lastResult, error, clearLastResult };
}
